import { CommonModule } from '@angular/common';
import { Component, computed, effect, inject, signal } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { Router } from '@angular/router';

import { Dialog } from 'primeng/dialog';
import { Select } from 'primeng/select';
import { Textarea } from 'primeng/textarea';
import { MessageService } from 'primeng/api';

import { WorkSessionStateService } from '../../../core/services/api/work-session-state.service';
import { ToolConditionRequest } from '../../../core/models/response';
import { AttachedFile, AttachmentUploaderComponent } from '../../../modules/attachment-uploader/attachment-uploader.component';

type ToolConditionValue = 'BUENO' | 'DANADO' | 'FALTANTE';

interface ToolConditionDraft {
    idTool: number;
    toolName: string;
    condition: ToolConditionValue;
    photoUrl: string;
    uploading: boolean;
    comment: string;
    reason: string;
}

/**
 * Flujo diario de herramientas para TECNICO/SUPERVISOR:
 *  - Bloquea la app hasta registrar el estado inicial de las herramientas.
 *  - Permite finalizar la jornada registrando el estado final.
 */
@Component({
    selector: 'app-work-session-gate',
    standalone: true,
    imports: [CommonModule, FormsModule, Dialog, Select, Textarea, AttachmentUploaderComponent],
    templateUrl: './work-session-gate.component.html',
    styleUrls: ['./work-session-gate.component.scss']
})
export class WorkSessionGateComponent {
    state = inject(WorkSessionStateService);
    private toast = inject(MessageService);
    private router = inject(Router);

    saving = signal(false);
    drafts = signal<ToolConditionDraft[]>([]);
    currentIndex = signal(0);
    /** Fuerza la recreación de los uploaders al reiniciar el formulario */
    formVersion = signal(0);

    conditionOptions = [
        { label: 'Buen estado', value: 'BUENO' },
        { label: 'Dañado', value: 'DANADO' },
        { label: 'Faltante', value: 'FALTANTE' }
    ];

    constructor() {
        // Reconstruye el borrador cuando cambian las herramientas asignadas
        effect(() => {
            const assignments = this.state.assignments();
            const currentIds = this.drafts().map((d) => d.idTool).join(',');
            const newIds = assignments.map((a) => a.idTool).join(',');
            if (currentIds !== newIds) {
                this.currentIndex.set(0);
                this.drafts.set(assignments.map((a) => this.emptyDraft(a.idTool, a.toolName)));
            }
        });

        // Al abrir el cierre de jornada se reinicia el registro final
        effect(() => {
            if (this.state.endDialogVisible()) {
                this.formVersion.update((v) => v + 1);
                this.currentIndex.set(0);
                this.drafts.set(this.state.assignments().map((a) => this.emptyDraft(a.idTool, a.toolName)));
            }
        });

        // Al pedir el inicio de una nueva jornada se reinicia el registro
        effect(() => {
            if (this.state.requiresStart()) {
                this.formVersion.update((v) => v + 1);
                this.currentIndex.set(0);
                this.drafts.set(this.state.assignments().map((a) => this.emptyDraft(a.idTool, a.toolName)));
            }
        });
    }

    private emptyDraft(idTool: number, toolName: string): ToolConditionDraft {
        return {
            idTool,
            toolName,
            condition: 'BUENO',
            photoUrl: '',
            uploading: false,
            comment: '',
            reason: ''
        };
    }

    private patchDraft(idTool: number, patch: Partial<ToolConditionDraft>): void {
        this.drafts.update((list) => list.map((d) => (d.idTool === idTool ? { ...d, ...patch } : d)));
    }

    setCondition(idTool: number, condition: ToolConditionValue): void {
        this.patchDraft(idTool, { condition });
    }

    setComment(idTool: number, comment: string): void {
        this.patchDraft(idTool, { comment });
    }

    setReason(idTool: number, reason: string): void {
        this.patchDraft(idTool, { reason });
    }

    onFilesChanged(idTool: number, files: AttachedFile[]): void {
        const file = files[0];
        this.patchDraft(idTool, {
            photoUrl: file?.proxyUrl || '',
            uploading: !!file?.uploading
        });
    }

    draftValid = (d: ToolConditionDraft): boolean =>
        !d.uploading &&
        (d.condition === 'FALTANTE' || !!d.photoUrl) &&
        (d.condition === 'BUENO' || !!d.reason.trim());

    allValid = computed(() => this.drafts().length > 0 && this.drafts().every((d) => this.draftValid(d)));

    currentDraft = computed(() => this.drafts()[this.currentIndex()] ?? null);
    currentDraftList = computed(() => {
        const draft = this.currentDraft();
        return draft ? [draft] : [];
    });
    currentValid = computed(() => {
        const draft = this.currentDraft();
        return !!draft && this.draftValid(draft);
    });
    isLastTool = computed(() => {
        const total = this.drafts().length;
        return total > 0 && this.currentIndex() === total - 1;
    });
    stepNumber = computed(() => Math.min(this.currentIndex() + 1, Math.max(this.drafts().length, 1)));
    progressPercent = computed(() => {
        const total = this.drafts().length;
        return total > 0 ? ((this.currentIndex() + 1) / total) * 100 : 0;
    });

    nextTool(): void {
        if (!this.currentValid() || this.isLastTool()) return;
        this.currentIndex.update((index) => index + 1);
    }

    previousTool(): void {
        if (this.currentIndex() > 0) {
            this.currentIndex.update((index) => index - 1);
        }
    }

    advanceStart(): void {
        if (this.isLastTool()) {
            this.submitStart();
        } else {
            this.nextTool();
        }
    }

    advanceEnd(): void {
        if (this.isLastTool()) {
            this.submitEnd();
        } else {
            this.nextTool();
        }
    }

    private buildConditions(): ToolConditionRequest[] {
        return this.drafts().map((d) => ({
            idTool: d.idTool,
            condition: d.condition,
            photoUrl: d.photoUrl || undefined,
            comment: d.comment || undefined,
            reason: d.reason || undefined
        }));
    }

    submitStart(): void {
        if (this.saving() || !this.allValid()) return;
        this.saving.set(true);
        this.state.start(this.buildConditions()).subscribe({
            next: () => {
                this.saving.set(false);
                this.toast.add({
                    key: 'toast',
                    severity: 'success',
                    summary: 'Jornada iniciada',
                    detail: 'Ya podés avanzar con el trabajo del día'
                });
                this.router.navigate(['/dashboard']);
            },
            error: (err) => {
                this.saving.set(false);
                this.toast.add({
                    key: 'toast',
                    severity: 'error',
                    summary: 'Error',
                    detail: err?.error?.message ?? 'No se pudo iniciar la jornada'
                });
            }
        });
    }

    submitEnd(): void {
        if (this.saving() || !this.allValid()) return;
        this.saving.set(true);
        this.state.end(this.buildConditions()).subscribe({
            next: () => {
                this.saving.set(false);
                this.toast.add({
                    key: 'toast',
                    severity: 'success',
                    summary: 'Jornada finalizada',
                    detail: 'La jornada fue cerrada correctamente'
                });
            },
            error: (err) => {
                this.saving.set(false);
                this.toast.add({
                    key: 'toast',
                    severity: 'error',
                    summary: 'Error',
                    detail: err?.error?.message ?? 'No se pudo finalizar la jornada'
                });
            }
        });
    }
}
